import '../styles/styles.css';

import { viewports as defaultViewports } from '@folklore/ads';
import { useRoutes } from '@folklore/routes';
import { SocketContainer } from '@folklore/socket';
import { Suspense, useEffect, lazy } from 'react';
import { Route, Switch } from 'wouter';

import useAdViewport from '../hooks/useAdViewport';

import { AdsProvider } from '../contexts/AdsContext';
import { UserAdsTargetingProvider } from '../contexts/AdsTargeting';
import { AudioPlayerProvider } from '../contexts/AudioPlayerContext';
import { AuthContextProvider } from '../contexts/AuthContext';
import { ConsentContextProvider } from '../contexts/ConsentContext';
import { DeviceContextProvider } from '../contexts/DeviceContext';
import { FacebookContextProvider } from '../contexts/FacebookContext';
import { GoogleContextProvider } from '../contexts/GoogleContext';
import { PopupsProvider } from '../contexts/PopupsContext';
import { PromotionsProvider } from '../contexts/PromotionsContext';
import { useSite } from '../contexts/SiteContext';
import { TrackingProvider } from '../contexts/TrackingContext';
// import { LayoutContextProvider } from '../contexts/LayoutContext';
import { VisitorContextProvider } from '../contexts/VisitorContext';
import slots from '../data/adsSlots';
import Html from './Html';
import * as richAdComponents from './ads/index';
import ReaderLayout from './layouts/ReaderLayout';
import TimelineLayout from './layouts/TimelineLayout';
import RedirectorPage from './pages/RedirectorPage';
import MiniAudioPlayer from './partials/MiniAudioPlayer';

import '@fortawesome/fontawesome-free/css/brands.css';
import '@fortawesome/fontawesome-free/css/fontawesome.css';

const ShareRoutes = lazy(() => import('./ShareRoutes'));

const defaultSlotsPath = {
    default: '/62442913/URBANIA',
    bumper: '/62442913/timeline',
};

export interface AppProps {
    isBot?: boolean;
    isRedirector?: boolean;
    presentation?: Presentation | null;
    tagManagerId?: string;
    adSlotsPath?: Record<string, string>;
    bncDCId?: string;
    facebookAppId?: string;
    googleClientId?: string;
    assetsManifest?: AssetsManifest | null;
    embeddedStyles?: string[] | null;
    embeddedScripts?: string | null;
    vendorsInHead?: boolean;
    withHtml?: boolean;
}

function App({
    isBot = false,
    isRedirector = false,
    adSlotsPath: initialSlotsPath = defaultSlotsPath,
    presentation = null,
    tagManagerId = 'GTM-WBRBHVQ',
    bncDCId = 'DC-9722614',
    facebookAppId = '1051760225433251',
    googleClientId = '432379609774-r4t5037c96fnp2ja3te6dq9i74dke3uq.apps.googleusercontent.com',
    assetsManifest = null,
    embeddedStyles = null,
    embeddedScripts = null,
    vendorsInHead = false,
    withHtml = false,
}: AppProps) {
    const routes = useRoutes();
    const { handle: siteHandle = null, adSlotsPath = initialSlotsPath, socket } = useSite();

    const isReader = presentation === 'reader' || presentation === 'capture';

    useEffect(() => {
        if (isRedirector || vendorsInHead || presentation === 'capture') {
            return;
        }

        const firstScript = document.getElementsByTagName('script')[0];

        // GTM
        const gtm = document.createElement('script');
        gtm.async = true;
        gtm.src = `https://www.googletagmanager.com/gtm.js?id=${tagManagerId}`;
        firstScript.parentNode.insertBefore(gtm, firstScript);

        // GPT
        const gpt = document.createElement('script');
        gpt.async = true;
        gpt.crossOrigin = 'anonymous';
        gpt.src = `https://securepubads.g.doubleclick.net/tag/js/gpt.js`;
        firstScript.parentNode.insertBefore(gpt, firstScript);

        // BNC
        const bnc = document.createElement('script');
        bnc.async = true;
        bnc.src = `https://www.googletagmanager.com/gtag/js?id=${bncDCId}&l=bncDataLayer`;
        firstScript.parentNode.insertBefore(bnc, firstScript);
    }, [tagManagerId, bncDCId, presentation]);

    const trackingVariables = {
        presentationMode: presentation,
        presentation,
    };

    const needsAdViewport = isReader;
    const adViewport = useAdViewport(defaultViewports);

    let page = null;
    if (isRedirector) {
        page = <RedirectorPage />;
    } else if (isReader) {
        page = <ReaderLayout presentation={presentation} />;
    } else {
        page = <TimelineLayout />;
    }
    // The audio player lives above the routes so listening goes on from page to page
    const inner = (
        <AudioPlayerProvider>
            <Switch>
                <Route path={routes['document.share.wildcard']}>
                    <Suspense fallback={null}>
                        <ShareRoutes />
                    </Suspense>
                </Route>
                <Route>{page}</Route>
            </Switch>
            <MiniAudioPlayer />
        </AudioPlayerProvider>
    );

    return (
        <SocketContainer {...socket} withPresence>
            <AuthContextProvider disabled={isRedirector} isBot={isBot}>
                <ConsentContextProvider>
                    <VisitorContextProvider disabled={isRedirector}>
                        <TrackingProvider disabled={isRedirector} variables={trackingVariables}>
                            <FacebookContextProvider
                                appId={facebookAppId}
                                locale={siteHandle === 'france' ? 'fr_FR' : 'fr_CA'}
                            >
                                <GoogleContextProvider clientId={googleClientId}>
                                    <PromotionsProvider>
                                        <AdsProvider
                                            slotsPath={adSlotsPath}
                                            slots={slots}
                                            disableTracking
                                            disabled={
                                                isRedirector ||
                                                (needsAdViewport && adViewport === null)
                                            }
                                            fetchMarginPercent={150}
                                            renderMarginPercent={100}
                                            richAdComponents={richAdComponents}
                                            viewport={needsAdViewport ? adViewport : null}
                                            // disableLazyLoad
                                            disableSingleRequest
                                            mobileScaling={1.5}
                                        >
                                            <DeviceContextProvider>
                                                <UserAdsTargetingProvider>
                                                    <PopupsProvider>
                                                        {withHtml ? (
                                                            <Html
                                                                assetsManifest={assetsManifest}
                                                                embeddedStyles={embeddedStyles}
                                                                embeddedScripts={embeddedScripts}
                                                                facebookAppId={facebookAppId}
                                                                bncDCId={bncDCId}
                                                                tagManagerId={tagManagerId}
                                                                loadVendors={vendorsInHead}
                                                                presentation={presentation}
                                                            >
                                                                {inner}
                                                            </Html>
                                                        ) : (
                                                            inner
                                                        )}
                                                    </PopupsProvider>
                                                </UserAdsTargetingProvider>
                                            </DeviceContextProvider>
                                        </AdsProvider>
                                    </PromotionsProvider>
                                </GoogleContextProvider>
                            </FacebookContextProvider>
                        </TrackingProvider>
                    </VisitorContextProvider>
                </ConsentContextProvider>
            </AuthContextProvider>
        </SocketContainer>
    );
}

export default App;
